import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { faSearch, faSpinner, faArrowLeft } from '@fortawesome/fontawesome-pro-regular';
import { SmsAbstractAppPage } from '../sms-abstract-app-page';
import { UsefHorseSearchService } from '../../providers/usef-horse-search.service';
import { UsefPersonSearchService } from '../../providers/usef-person-search.service';
import { EcHorseSearchService } from '../../providers/ec-horse-search.service';
import { EcPersonSearchService } from '../../providers/ec-person-search.service';
import { Usef_horse } from '../../models/usef/usef_horse';
import { Usef_person } from '../../models/usef/usef_person';
import { Ec_horse } from '../../models/ec/ec_horse';
import { Ec_member } from '../../models/ec/ec_member';

@Component({
  standalone: false,
  selector: 'app-modal-choose-federation',
  templateUrl: './modal-choose-federation.component.html',
  styleUrls: ['./modal-choose-federation.component.css']
})
export class ModalChooseFederationComponent extends SmsAbstractAppPage implements OnInit {
  @Input() entity_type: string = 'Horse';
  @Input() event_federation_code: string;
  @Input() country_iso_3: string;

  @Output() usef_horse_selected$: EventEmitter<Usef_horse> = new EventEmitter<Usef_horse>();
  @Output() usef_person_selected$: EventEmitter<Usef_person> = new EventEmitter<Usef_person>();
  @Output() ec_horse_selected$: EventEmitter<Ec_horse> = new EventEmitter<Ec_horse>();
  @Output() ec_member_selected$: EventEmitter<Ec_member> = new EventEmitter<Ec_member>();
  @Output() skipped$: EventEmitter<boolean> = new EventEmitter<boolean>();

  faSearch = faSearch;
  faSpinner = faSpinner;
  faArrowLeft = faArrowLeft;

  step: 'choose' | 'search' = 'choose';
  federation_mode: 'usef' | 'ec' = 'usef';
  loading: boolean = false;
  horse_results: Usef_horse[] = [];
  person_results: Usef_person[] = [];
  ec_horse_results: Ec_horse[] = [];
  ec_member_results: Ec_member[] = [];
  person_entity_type_filter: string = 'I';
  selected_state: string = '';

  get is_canadian(): boolean {
    return this.country_iso_3 === 'CAN';
  }

  constructor(
    public activeModal: NgbActiveModal,
    private usefHorseSearchService: UsefHorseSearchService,
    private usefPersonSearchService: UsefPersonSearchService,
    private ecHorseSearchService: EcHorseSearchService,
    private ecPersonSearchService: EcPersonSearchService
  ) {
    super();
  }

  ngOnInit() {}

  handleSelectUsef(): void {
    this.federation_mode = 'usef';
    this.step = 'search';
  }

  handleSelectEc(): void {
    this.federation_mode = 'ec';
    this.step = 'search';
  }

  handleSkip(): void {
    this.skipped$.emit(true);
  }

  handleBack(): void {
    this.step = 'choose';
    this.federation_mode = 'usef';
    this.horse_results = [];
    this.person_results = [];
    this.ec_horse_results = [];
    this.ec_member_results = [];
    this.alerts = [];
  }

  // ---- USEF horse search ----

  handleUsefHorseIdSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.horse_results = [];
    this.usefHorseSearchService.getById(term).then(results => {
      this.loading = false;
      this.horse_results = results;
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleUsefHorseNameSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.horse_results = [];
    this.usefHorseSearchService.getByName(term).then(results => {
      this.loading = false;
      this.horse_results = results;
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleSelectHorse(usef_horse: Usef_horse): void {
    this.usef_horse_selected$.emit(usef_horse);
  }

  // ---- USEF person search ----

  handleUsefPersonIdSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.person_results = [];
    this.usefPersonSearchService.getById(term, this.person_entity_type_filter).then(results => {
      this.loading = false;
      this.person_results = results;
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleUsefPersonNameSearch(first_name: string, last_name: string, city: string, state: string): void {
    if (!first_name.trim() && !last_name.trim()) return;
    this.loading = true;
    this.person_results = [];
    this.usefPersonSearchService.getByName(first_name, last_name, city, state).then(results => {
      this.loading = false;
      this.person_results = results;
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleSelectPerson(usef_person: Usef_person): void {
    this.usef_person_selected$.emit(usef_person);
  }

  // ---- EC horse search ----

  handleEcHorseIdSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.ec_horse_results = [];
    this.ecHorseSearchService.getById(term).then(result => {
      this.loading = false;
      this.ec_horse_results = result ? [result] : [];
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleEcHorseNameSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.ec_horse_results = [];
    this.ecHorseSearchService.getByName(term).then(results => {
      this.loading = false;
      this.ec_horse_results = Array.isArray(results) ? results : [results];
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleSelectEcHorse(ec_horse: Ec_horse): void {
    this.ec_horse_selected$.emit(ec_horse);
  }

  // ---- EC person search ----

  handleEcPersonIdSearch(term: string): void {
    if (!term.trim()) return;
    this.loading = true;
    this.ec_member_results = [];
    this.ecPersonSearchService.getById(term).then(result => {
      this.loading = false;
      this.ec_member_results = result ? [result] : [];
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleEcPersonNameSearch(last_name: string): void {
    if (!last_name.trim()) return;
    this.loading = true;
    this.ec_member_results = [];
    this.ecPersonSearchService.getByLastName(last_name).then(results => {
      this.loading = false;
      this.ec_member_results = Array.isArray(results) ? results : [results];
    }).catch(error => {
      this.loading = false;
      this.handleError(error);
    });
  }

  handleSelectEcMember(ec_member: Ec_member): void {
    this.ec_member_selected$.emit(ec_member);
  }

  // ---- Display helpers ----

  personInitials(p: Usef_person): string {
    if (p.entity_type === 'I') {
      return ((p.first_name || '').charAt(0) + (p.last_name || '').charAt(0)).toUpperCase() || '?';
    }
    return ((p.name || '?').charAt(0)).toUpperCase();
  }

  personDisplayName(p: Usef_person): string {
    if (p.entity_type === 'I') {
      return [p.prefix, p.first_name, p.middle_name, p.last_name, p.suffix]
        .filter(Boolean).join(' ');
    }
    return p.name || '';
  }

  entityTypeLabel(entity_type: string): string {
    switch (entity_type) {
      case 'I': return 'Individual';
      case 'O': return 'Organization';
      case 'F': return 'Farm';
      default: return entity_type;
    }
  }
}
